import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FileUploadMapping from '@/components/mapping/FileUploadMapping';
import MappingTable, { MappingConfiguration } from '@/components/mapping/MappingTable';
import VendorSelect from '@/components/VendorSelect';
import ProductFeedHistory from '@/pages/product-feed-management-2/components/ProductFeedHistory';
import { FileProcessingResponse } from '@/services/spreadsheetService';
import { getProductFeedPresignedUrl, uploadFileWithPresignedUrl, submitProductFeedAttributes, fetchFileProcessStatus, processNextStep } from '@/services/api';
import { toast } from 'sonner';
import { Vendor, ImageConfig } from '@/types';
import React from 'react';

/**
 * ProductFeedManagement Component
 * 
 * Handles the complete product feed upload and mapping process:
 * 1. Vendor selection
 * 2. File upload and processing
 * 3. Attribute mapping configuration
 * 4. File upload to presigned URL
 * 5. Mapping configuration submission
 */
const ProductFeedManagement = () => {
  const [supplierHeaders, setSupplierHeaders] = useState<string[]>([]);
  const [showMappingTable, setShowMappingTable] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [activeTab, setActiveTab] = useState('upload');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState<string>('');

  // Image Processing Configuration State
  const [vendorStorageLocationType, setVendorStorageLocationType] = useState<string>('Hosted Images');
  const [nestingaleS3Location, setNestingaleS3Location] = useState<string>('');
  const [imageNamingSyntax, setImageNamingSyntax] = useState<string>('SKU_');
  const [dropboxFolderStructureType, setDropboxFolderStructureType] = useState<string>('Folder Structure By SKU');
  const [folderUrl, setFolderUrl] = useState<string>('');
  const [sharedFolderUrl, setSharedFolderUrl] = useState<string>('');
  const [searchImagesPattern, setSearchImagesPattern] = useState<string>('skuname*');

  // Update Nestingale S3 Location when vendor changes
  React.useEffect(() => {
    if (selectedVendor) {
      setNestingaleS3Location(`/<${selectedVendor.name}>`);
    } else {
      setNestingaleS3Location('');
    }
  }, [selectedVendor]);

  const handleVendorSelect = (vendor: Vendor | null) => {
    setSelectedVendor(vendor);
  };

  const handleFileProcessed = (data: FileProcessingResponse & { file: File }) => {
    if (!selectedVendor) {
      toast.error('Please select a vendor before uploading a file');
      return;
    }
    
    setSupplierHeaders(data.headers);
    setUploadedFile(data.file);
    setShowMappingTable(true);
    toast.success('File processed successfully. You can now configure mappings.');
  };

  const handleMappingSubmit = async (mappings: MappingConfiguration[]) => {
    if (!selectedVendor || !uploadedFile) {
      toast.error('Missing vendor or file information');
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);
    setCurrentStep('Getting presigned URL...');
    
    try {
      // Get presigned URL for file upload
      // Use the actual file extension for the template name
      const fileExtension = uploadedFile.name.split('.').pop()?.toLowerCase();
      const templateName = fileExtension === 'xlsx' || fileExtension === 'xls' 
        ? `${selectedVendor.name}.xlsx` 
        : `${selectedVendor.name}.csv`;
      
      console.log('=== GETTING PRESIGNED URL ===');
      console.log('File name:', uploadedFile.name);
      console.log('Vendor:', selectedVendor.name);
      console.log('Template name:', templateName);
      
      const presignedUrlResponse = await getProductFeedPresignedUrl(
        uploadedFile.name,
        selectedVendor.name,
        true,
        templateName
      );
      
      console.log('Presigned URL response:', presignedUrlResponse);

      setCurrentStep('Uploading file...');
      setUploadProgress(50); // Set to 50% since we can't track progress with fetch
      
      // Upload the file to the presigned URL
      await uploadFileWithPresignedUrl(
        presignedUrlResponse.preSignedUrl,
        uploadedFile,
        uploadedFile.name,
        selectedVendor.name
      );
      
      setUploadProgress(100); // Set to 100% after upload completes

      setCurrentStep('Submitting mapping configuration...');
      
      // Transform mappings to the expected API format
      const nestingaleMap: Record<string, string | string[]> = {};
      
      mappings.forEach(mapping => {
        if (mapping.supplierAttributes.length === 1) {
          // Single attribute maps to a string
          nestingaleMap[mapping.nestingaleAttribute] = mapping.supplierAttributes[0];
        } else {
          // Multiple attributes map to an array
          nestingaleMap[mapping.nestingaleAttribute] = mapping.supplierAttributes;
        }
      });

      // Build image configuration
      const imageConfig: ImageConfig = {
        vendorStorageLocation: vendorStorageLocationType === 'Hosted Images' ? 'hosted' : 
                              vendorStorageLocationType === 'Dropbox' ? 'dropbox' : 
                              vendorStorageLocationType === 'Google Drive' ? 'googleDrive' : 'hosted',
        nestingaleLocation: `s3://vendor-sftp-storage/${selectedVendor.name}/`,
        imgNameSyntax: imageNamingSyntax === 'SKU_' ? 'SKU_Index' : 'saveAsIs',
        dropBox: {
          type: dropboxFolderStructureType === 'Folder Structure By SKU' ? 'bySku' : 'shared',
          ...(dropboxFolderStructureType === 'Folder Structure By SKU' && folderUrl && {
            folderColumnName: folderUrl
          }),
          ...(dropboxFolderStructureType === 'Shared Folder' && sharedFolderUrl && {
            sharedFolderURL: sharedFolderUrl
          }),
          imagePattern: searchImagesPattern === 'skuname*' ? 'skuname_*' : '*_skuname'
        }
      };

      console.log('=== IMAGE CONFIGURATION ===');
      console.log('Image config being sent:', JSON.stringify(imageConfig, null, 2));

      // Submit the mapping configuration with image config
      console.log('=== SUBMITTING MAPPING CONFIGURATION ===');
      console.log('Vendor:', selectedVendor.name);
      console.log('Nestingale map:', JSON.stringify(nestingaleMap, null, 2));
      console.log('Image config:', JSON.stringify(imageConfig, null, 2));
      
      await submitProductFeedAttributes(selectedVendor.name, nestingaleMap);
      
      setCurrentStep('Complete!');
      toast.success('File uploaded and mapping configuration submitted successfully!');
      console.log('Submitted mappings:', mappings);
      
      // Reset the form
      setShowMappingTable(false);
      setUploadedFile(null);
      setSupplierHeaders([]);
      setUploadProgress(0);
      setCurrentStep('');
      
    } catch (error) {
      console.error('Submission failed:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to upload file and submit mapping configuration';
      if (error instanceof Error) {
        if (error.message.includes('SignatureDoesNotMatch')) {
          errorMessage = 'Upload failed due to signature mismatch. Please try again.';
        } else if (error.message.includes('403')) {
          errorMessage = 'Upload failed due to permission issues. Please check your access.';
        } else if (error.message.includes('404')) {
          errorMessage = 'Upload failed - URL not found. Please try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      toast.error(errorMessage);
      setCurrentStep('Error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const handleGoToHistory = () => {
    setActiveTab('history');
  };

  return (
    <div className="max-w-4xl">
      <h2 className="text-2xl font-bold text-gray-900 mb-8">Product Feed Management</h2>
      
      <Tabs 
        value={activeTab}
        onValueChange={handleTabChange} 
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="upload">Upload Product Feed</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>
        
        <TabsContent value="upload">
          {!showMappingTable ? (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Select Vendor</CardTitle>
                  <CardDescription>
                    Choose the vendor for this product feed upload
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <VendorSelect onVendorSelect={setSelectedVendor} />
                </CardContent>
              </Card>



              <Card className="border-2 border-dashed border-gray-300">
                <CardContent className="p-12">
                  {selectedVendor ? (
                    <FileUploadMapping onFileProcessed={handleFileProcessed} />
                  ) : (
                    <div className="text-center text-gray-500">
                      <p className="text-lg font-medium mb-2">Please select a vendor first</p>
                      <p className="text-sm">You must choose a vendor before uploading a file</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="space-y-6">
              <Card>
                <CardHeader>                  
                  {/* <CardTitle>Map Supplier Attributes to Nestingale Attributes</CardTitle> */}
                  <CardTitle className="flex items-center justify-between">
                    Image Processing Configurations
                    <button className="text-gray-400 hover:text-gray-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="vendorStorageLocationType">Vendor Storage Location Type</Label>
                      <Select value={vendorStorageLocationType} onValueChange={setVendorStorageLocationType}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Hosted Images">Hosted Images</SelectItem>
                          <SelectItem value="Dropbox">Dropbox</SelectItem>
                          <SelectItem value="Google Drive">Google Drive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="nestingaleS3Location">Nestingale S3 Location</Label>
                      <Input
                        id="nestingaleS3Location"
                        value={nestingaleS3Location}
                        readOnly
                        className="bg-gray-50"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="imageNamingSyntax">Image Naming Syntax</Label>
                      <Select value={imageNamingSyntax} onValueChange={setImageNamingSyntax}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SKU_">SKU_index</SelectItem>
                          <SelectItem value="Save As Is">Save As Is</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Conditional Dropbox Configuration */}
                  {vendorStorageLocationType === 'Dropbox' && (
                    <div className="mt-6 space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="dropboxFolderStructureType">Dropbox Folder Structure Type</Label>
                        <Select value={dropboxFolderStructureType} onValueChange={setDropboxFolderStructureType}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Folder Structure By SKU">Folder Structure By SKU</SelectItem>
                            <SelectItem value="Shared Folder">Shared Folder</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {dropboxFolderStructureType === 'Folder Structure By SKU' && (
                        <div className="space-y-2">
                          <Label htmlFor="folderUrl">Folder URL</Label>
                          <Select value={folderUrl} onValueChange={setFolderUrl}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a column from your dataset" />
                            </SelectTrigger>
                            <SelectContent>
                              {supplierHeaders.map((header) => (
                                <SelectItem key={header} value={header}>
                                  {header}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {dropboxFolderStructureType === 'Shared Folder' && (
                        <div className="space-y-2">
                          <Label htmlFor="sharedFolderUrl">Shared Folder URL</Label>
                          <Input
                            id="sharedFolderUrl"
                            value={sharedFolderUrl}
                            onChange={(e) => setSharedFolderUrl(e.target.value)}
                            placeholder="Enter shared folder URL"
                          />
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label htmlFor="searchImagesPattern">Search Images Pattern</Label>
                        <Select value={searchImagesPattern} onValueChange={setSearchImagesPattern}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="skuname*">skuname*</SelectItem>
                            <SelectItem value="_skuname">_skuname</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    Map Supplier Attributes to Nestingale Attributes
                    <button className="text-gray-400 hover:text-gray-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </CardTitle>
                  <CardDescription>
                    Configure how supplier attributes map to Nestingale attributes and set validation rules for {selectedVendor?.displayName}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isSubmitting && (
                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm font-medium text-blue-800">{currentStep}</span>
                      </div>
                      {uploadProgress > 0 && uploadProgress < 100 && (
                        <div className="w-full bg-blue-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                      )}
                    </div>
                  )}
                  <MappingTable 
                    supplierHeaders={supplierHeaders}
                    onSubmit={handleMappingSubmit}
                    vendorName={selectedVendor?.name}
                    isSubmitting={isSubmitting}
                  />
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="history">
          <ProductFeedHistory 
            selectedVendor={selectedVendor} 
            onVendorSelect={handleVendorSelect}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProductFeedManagement;